import React, { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { authAPI } from './api';
import './styles/global.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
// Password reset (Sep 2026 — new): the email-reset link built in
// LoginPage.js's requestPasswordReset flow points at
// `${FRONTEND_URL}/reset-password?token=...` — this route didn't exist
// at all before (confirmed while wiring the reset flow), so the link
// had nowhere to land. Reuses LoginPage.js itself, which already reads
// a ?token= query param via useLocation() and shows its
// 'forgot-set-new-password' view directly when one is present.
import AdminLoginPage from './pages/admin/AdminLoginPage';
import { Spinner } from './components/common/index';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './components/LandingPage';
import { LegalDocumentPage } from './components/LegalDocuments';
import { LEGAL_DOCUMENTS } from './content/legalDocuments';
import { useAutoFitPlaceholders } from './utils/fitPlaceholder';
// English / Hindi for patient pages (13 Oct 2026); doctor and admin stay English.
import { LanguageProvider, useI18n } from './i18n';
// The big sections load only when someone opens them (2 Oct 2026), so the
// home page and login download much less: registration, the patient,
// doctor and admin portals each come as their own part.
const RegisterPage = lazy(() => import('./pages/patient/RegisterPage'));
const DoctorRegisterPage = lazy(() => import('./pages/doctor/DoctorRegisterPage'));
const PatientPortal = lazy(() => import('./pages/patient/PatientPortal'));
const DoctorPortal = lazy(() => import('./pages/doctor/DoctorPortal'));
const AdminPortal = lazy(() => import('./pages/admin/AdminPortal'));

const PageLoading = () => (
  <div className="loading-screen">
    <Spinner size={64} borderColor="#000" stripColor="var(--teal-400)" />
  </div>
);

const EXIT_WARNING_WINDOW_MS = 2500;

// The address without the one-time PayU return details (?payment=&ep=&cond=)
// — the guard's extra history entries must not carry them, or Back then
// Refresh shows "Payment successful" again (audit 6 Oct 2026).
const guardUrl = () => {
  const u = new URL(window.location.href);
  ['payment', 'ep', 'cond'].forEach(k => u.searchParams.delete(k));
  return `${u.pathname}${u.search}${u.hash}`;
};

const BackButtonExitGuard = () => {
  const { t } = useI18n();
  const [showExitWarning, setShowExitWarning] = useState(false);
  const armedRef = useRef(false);
  const warningTimerRef = useRef(null);

  useEffect(() => {
    window.history.pushState({ backGuard: true }, '', guardUrl());

    const handlePopState = () => {
      if (armedRef.current) {
        armedRef.current = false;
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        setShowExitWarning(false);
        return;
      }
      armedRef.current = true;
      window.history.pushState({ backGuard: true }, '', guardUrl());
      setShowExitWarning(true);
      warningTimerRef.current = setTimeout(() => {
        armedRef.current = false;
        setShowExitWarning(false);
      }, EXIT_WARNING_WINDOW_MS);
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, []);

  if (!showExitWarning) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(20,20,20,0.9)', color: '#fff', padding: '12px 22px',
      borderRadius: 10, fontSize: 13, zIndex: 3000, textAlign: 'center',
      maxWidth: 300, boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
    }}>
      {t('Do you really want to exit? Press back button once again.')}
    </div>
  );
};

// Grey hints inside boxes shrink to fit in full on every page, desktop and
// mobile; typed text stays normal size (4 Oct 2026 — see utils/fitPlaceholder.js).
const AutoFitPlaceholders = () => {
  useAutoFitPlaceholders();
  return null;
};

const AuthedBackButtonGuard = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <BackButtonExitGuard />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-screen">
        <Spinner size={64} borderColor="#000" stripColor="var(--teal-400)" />
      </div>
    );
  }

  // Remember the page, so login comes back to it (audit 9 Oct 2026) —
  // e.g. the PayU return address when the session had ended.
  if (!user) return <Navigate to="/login" replace state={{ from: { pathname: location.pathname, search: location.search } }} />;

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'doctor') return <Navigate to="/doctor/dashboard" replace />;
    if (user.role === 'admin' || user.role === 'super_admin') return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to="/patient/dashboard" replace />;
  }

  return children;
};

// Logged-out visitors at "/" now see LandingPage instead of being bounced
// straight to /login — everything else (loading state, role-based
// redirects for signed-in users) is unchanged from before.
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><Spinner size={64} borderColor="#000" stripColor="var(--teal-400)" /></div>;
  // A registration still in progress in this tab (signed in by the
  // registration itself, not by Login) goes back to it (audit 9 Oct 2026).
  if (!user) return authAPI.getToken() ? <Navigate to="/register?resume=1" replace /> : <LandingPage />;
  if (user.role === 'doctor') return <Navigate to="/doctor/dashboard" replace />;
  if (user.role === 'admin' || user.role === 'super_admin') return <Navigate to="/admin/dashboard" replace />;
  return <Navigate to="/patient/dashboard" replace />;
};

const App = () => {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <LanguageProvider>
      <AuthProvider>
        <AuthedBackButtonGuard />
        <AutoFitPlaceholders />
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Dedicated entry points (Sept 2026) — each remembers a
              "channel" in localStorage (see LoginPage.js/ConditionSelection.js)
              that filters what "+ Add Condition" offers afterward. Not a
              permanent per-account setting: logging in via plain /login,
              or via the other channel URL, changes it again. */}
          <Route path="/login/thyroidproblems" element={<LoginPage channel="thyroid" />} />
          <Route path="/login/otherproblems" element={<LoginPage channel="other" />} />
          <Route path="/reset-password" element={<LoginPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* Doctor self-registration (24 Sep 2026) — public; more specific
              than the protected /doctor/* below, so it matches first. */}
          <Route path="/doctor/register" element={<DoctorRegisterPage />} />

          <Route path="/patient/*" element={
            <ProtectedRoute allowedRoles={['patient']}>
              <PatientPortal />
            </ProtectedRoute>
          } />

          <Route path="/doctor/*" element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <DoctorPortal />
            </ProtectedRoute>
          } />

          <Route path="/admin/*" element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <AdminPortal />
            </ProtectedRoute>
          } />

          {/* Policies — public, no login (28 Sep 2026); also linked from
              the "Who are you registering for?" page. */}
          {/* Public pages: About Us, Our Doctors, policies, Contact Us
              (one entry each in content/legalDocuments.js). */}
          {Object.entries(LEGAL_DOCUMENTS).map(([key, d]) => (
            <Route key={key} path={d.path} element={<LegalDocumentPage docKey={key} />} />
          ))}
          <Route path="/" element={<RootRedirect />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
